import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenantLocation, useMoney } from '../context/LocationContext';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { RefreshCw, Search, Loader2, Package, ArrowRight } from 'lucide-react';

export default function Products() {
  const { token } = useAuth();
  const { currentLocationId } = useTenantLocation();
  const { money } = useMoney();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [syncStatusFilter, setSyncStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (syncStatusFilter !== 'all') params.sync_status = syncStatusFilter;
      const response = await api.getProducts(token, params);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, categoryFilter, syncStatusFilter, currentLocationId]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await api.syncProducts(token);
      toast.success(result.data.message);
      fetchProducts();
    } catch (error) {
      toast.error('Product sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const getSyncStatusBadge = (status) => {
    const styles = {
      synced: 'status-success',
      failed: 'status-error',
      pending: 'status-pending'
    };
    return styles[status] || 'status-pending';
  };

  const categories = [...new Set(products.map(p => p.category))];
  
  const filteredProducts = products.filter(product => 
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.sku.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-white tracking-tight">
            Products
          </h1>
          <p className="text-slate-400 mt-1">Sync products from Odoo to UnTill POS</p>
        </div>
        <Button 
          onClick={handleSync}
          disabled={syncing}
          className="bg-blue-500 hover:bg-blue-600 text-white"
          data-testid="sync-products-btn"
        >
          {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Sync Products
        </Button>
      </div>

      {/* Sync Direction Indicator */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-4">
          <div className="flex items-center justify-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <span className="text-emerald-400 font-medium">Odoo</span>
              <span className="text-xs text-emerald-500/60">(Master)</span>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-500" />
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <span className="text-blue-400 font-medium">UnTill POS</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-white">{products.length}</p>
            <p className="text-slate-400 text-sm">Total Products</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-400">{products.filter(p => p.sync_status === 'synced').length}</p>
            <p className="text-slate-400 text-sm">Synced</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-400">{products.filter(p => p.sync_status === 'pending').length}</p>
            <p className="text-slate-400 text-sm">Pending</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-rose-400">{products.filter(p => p.sync_status === 'failed').length}</p>
            <p className="text-slate-400 text-sm">Failed</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Search by name or SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                data-testid="products-search-input"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px] bg-slate-800/50 border-slate-700 text-white" data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={syncStatusFilter} onValueChange={setSyncStatusFilter}>
              <SelectTrigger className="w-[180px] bg-slate-800/50 border-slate-700 text-white" data-testid="sync-status-filter">
                <SelectValue placeholder="Sync Status" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="synced">Synced</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No products found. Seed demo data from the dashboard.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-slate-400">SKU</TableHead>
                  <TableHead className="text-slate-400">Name</TableHead>
                  <TableHead className="text-slate-400">Category</TableHead>
                  <TableHead className="text-slate-400">Price</TableHead>
                  <TableHead className="text-slate-400">Odoo ID</TableHead>
                  <TableHead className="text-slate-400">UnTill ID</TableHead>
                  <TableHead className="text-slate-400">Sync Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map((product) => (
                  <TableRow key={product.id} className="border-slate-800 data-table-row">
                    <TableCell className="font-mono text-white">{product.sku}</TableCell>
                    <TableCell className="text-slate-300">{product.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-slate-700 text-slate-300">
                        {product.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-white font-medium">{money(product.price, product.location_id)}</TableCell>
                    <TableCell className="font-mono text-slate-400 text-sm">{product.odoo_id}</TableCell>
                    <TableCell className="font-mono text-slate-400 text-sm">{product.utill_id}</TableCell>
                    <TableCell>
                      <Badge className={getSyncStatusBadge(product.sync_status)}>{product.sync_status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
